
import { DropResult } from "@hello-pangea/dnd";
import { Box, Button, Dialog, DialogActions, DialogContent, InputAdornment, MenuItem, TextField, Typography, useTheme } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Field, Form, Formik } from "formik";
import { awsImageRootUrl } from "lib/constants";
import { Business } from "lib/features/business/types";
import { useUploadImageMutation } from "lib/features/s3/s3Service";
import { UploadImageRequest } from "lib/features/s3/types";
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { getCroppedImage } from "util/cropImage";
import { v4 as uuid } from "uuid";
import * as Yup from "yup";
import CalendarIcon from "../../../assets/icons/calendar_icon.svg";
import Label from "../NewItemForm/components/Label";

export type QuickLinkFormValues = {
    item_name: string;
    date: string | null;
    start_time: string | null;
    quantity: number;
    duration_days: number | null;
    duration_hours: number | null;
    duration_minutes: number | null;
    description: string;
    whats_included: string;
    cancellation_policy: string;
    price: number;
    commission: number;
    customer_added_value: string;
    itemImages: string[];
}

export type QuickLinkFormProps = {
    createQuickLinkFormValues: QuickLinkFormValues;
    setCreateQuickLinkFormValues: Dispatch<SetStateAction<QuickLinkFormValues>>;
    onSubmit: (values: QuickLinkFormValues) => void;
    businessData?: Business[];
}

const quickLinkValidation = Yup.object({
    item_name: Yup.string().required("Item name is required"),
    date: Yup.string().required("Date is required"),
    start_time: Yup.string().required("Start time is required"),
    quantity: Yup.number()
        .nullable()
        .typeError("Quantity must be a number")
        .min(1, "Quantity must be at least 1"),
    duration_days: Yup.number().nullable(),
    duration_hours: Yup.number().nullable(),
    duration_minutes: Yup.number().nullable(),
    description: Yup.string().required("Description is required"),
    whats_included: Yup.string().required("What's included is required"),
    cancellation_policy: Yup.string().required("Cancellation policy is required"),
    price: Yup.number()
        .nullable()
        .typeError("Price must be a number")
        .min(0, "Price cannot be negative"),
    commission: Yup.number()
        .nullable()
        .typeError("Commission must be a number")
        .min(0, "Commission cannot be negative"),
    customer_added_value: Yup.string().nullable(),
    itemImages: Yup.array().min(1, "At least one image is required"),
});

// Generate time options with 30-minute increments starting at 12:00 PM
export const generateTimeOptions = () => {
    const times: string[] = [];
    const totalMinutes = 24 * 60;
    // Start at 12:00 PM (720 minutes from midnight)
    for (let i = 0; i < totalMinutes / 30; i++) {
        const minutes = (720 + i * 30) % totalMinutes;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const period = hours >= 12 ? "AM" : "PM";
        const displayHour = hours % 12 === 0 ? 12 : hours % 12;
        const displayMins = mins.toString().padStart(2, "0");
        times.push(`${displayHour}:${displayMins} ${period}`);
    }
    return times;
};


// Duration options
const dayOptions = Array.from({ length: 31 }, (_, i) => i);
const hourOptions = Array.from({ length: 24 }, (_, i) => i);
const minuteOptions = [0, 15, 30, 45];

export const QuickLinkForm = ({ createQuickLinkFormValues, setCreateQuickLinkFormValues, onSubmit, businessData }: QuickLinkFormProps) => {
    const theme = useTheme();
    const [openCropper, setOpenCropper] = useState(false);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
    const [uploadImage] = useUploadImageMutation();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [hasAutoLoadedImage, setHasAutoLoadedImage] = useState<boolean>(false);
    const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
    const [firstTimeQuantityClick, setFirstTimeQuantityClick] = useState<boolean>(true)
    const timeOptions = useMemo(() => generateTimeOptions(), []);
    // Auto-load business profile picture on mount
    useEffect(() => {
        if (!hasAutoLoadedImage && businessData?.[0]?.businessImages?.[0]?.uri) {
            const businessImageUri = businessData[0].businessImages[0].uri;
            if (createQuickLinkFormValues.itemImages.length === 0) {
                setCreateQuickLinkFormValues((prev) => ({
                    ...prev,
                    itemImages: [businessImageUri],
                }));
                setHasAutoLoadedImage(true);
            }
        }
    }, [businessData, createQuickLinkFormValues.itemImages.length, setCreateQuickLinkFormValues, hasAutoLoadedImage]);

    const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleCropConfirm = async (setFieldValue: any) => {
        if (!imageSrc || !croppedAreaPixels) return;
        try {
            const croppedBase64 = await getCroppedImage(imageSrc, croppedAreaPixels);
            if (croppedBase64) {
                const response = await resizeAndUploadImage(croppedBase64);
                if (response) {
                    const imageUrl = `${response}`;

                    // Update Formik values
                    const updatedImages = [...createQuickLinkFormValues.itemImages, imageUrl];
                    setFieldValue("itemImages", updatedImages);

                    // Update global state
                    setCreateQuickLinkFormValues((prevValues: any) => ({
                        ...prevValues,
                        itemImages: updatedImages,
                    }));
                }
            }
        } catch (err) {
            console.error("Crop/Upload failed:", err);
        } finally {
            setOpenCropper(false);
        }
    };

    const resizeAndUploadImage = async (image: string) => {
        return new Promise((resolve, reject) => {
            const id = uuid() + ".jpg";
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext("2d");

            if (ctx && canvas) {
                const img = new Image();
                img.onload = async () => {
                    const targetWidth = 1080;
                    const scaleFactor = targetWidth / img.width;
                    const targetHeight = img.height * scaleFactor;

                    canvas.width = targetWidth;
                    canvas.height = targetHeight;

                    ctx.clearRect(0, 0, targetWidth, targetHeight);
                    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

                    // Convert canvas image to base64
                    const base64String = canvas.toDataURL("image/jpeg").split(",")[1];
                    const fileSize = atob(base64String).length;

                    const payload: UploadImageRequest = {
                        id,
                        image: {
                            base64: base64String,
                            width: targetWidth,
                            height: targetHeight,
                            fileSize,
                            uri: "",
                            type: "image/jpeg",
                            fileName: id,
                        },
                    };

                    try {
                        const result = await uploadImage(payload);
                        resolve(result?.data);
                    } catch (error) {
                        console.log("Upload error:", error);
                        reject(error);
                    }
                };

                img.onerror = (err) => reject("Error loading image.");
                img.src = image;
            } else {
                reject("Canvas context is not available.");
            }
        });
    };

    // Calculate payout
    const calculatePayout = (price: number, commission: number): string => {
        if (!price || price <= 0) return "$0";
        const payoutAmount = price - (0.05 * price) - (price * (commission || 0)) / 100;
        return `$${Math.max(0, payoutAmount).toFixed(2)}`;
    };
    return (
        <Formik
            initialValues={{ ...createQuickLinkFormValues, start_time: timeOptions[24] }}
            validationSchema={quickLinkValidation}
            onSubmit={onSubmit}
            enableReinitialize
        >
            {({ values, errors, touched, setFieldValue }) => {
                const handleChange = (e: React.ChangeEvent<any>) => {
                    const { name, value } = e.target;
                    setFieldValue(name, value);
                    setCreateQuickLinkFormValues((prevValues: any) => ({
                        ...prevValues,
                        [name]: value,
                    }));
                };

                const handleNumericChange = (
                    e: React.ChangeEvent<any>,
                    fieldName: string
                ) => {
                    const { value } = e.target;
                    if (/^\d*$/.test(value) || value === "") {
                        const numericValue = value === "" ? null : Number(value);

                        // Update Formik State
                        setFieldValue(fieldName, numericValue);

                        // Persist Data to Global State
                        setCreateQuickLinkFormValues((prevValues: any) => ({
                            ...prevValues,
                            [fieldName]: numericValue,
                        }));
                    }
                };

                const handleSelectChange = (fieldName: string, value: any) => {
                    setFieldValue(fieldName, value);
                    setCreateQuickLinkFormValues((prevValues: any) => ({
                        ...prevValues,
                        [fieldName]: value,
                    }));
                };

                const handleImageUpload = async (
                    event: React.ChangeEvent<HTMLInputElement>
                ) => {
                    const file = event.target.files?.[0];
                    if (file && file.type.startsWith("image/")) {
                        const reader = new FileReader();
                        reader.onload = async (e: ProgressEvent<FileReader>) => {
                            if (e.target?.result) {
                                // Open cropper with the raw image
                                setImageSrc(e.target.result as string);
                                setOpenCropper(true);
                            }
                        };
                        reader.readAsDataURL(file);
                    }
                };

                const handleDragEnd = (result: DropResult) => {
                    if (!result.destination) return;

                    const reordered = Array.from(values.itemImages);
                    const [moved] = reordered.splice(result.source.index, 1);
                    reordered.splice(result.destination.index, 0, moved);

                    setFieldValue("itemImages", reordered);
                    setCreateQuickLinkFormValues((prevValues: any) => ({
                        ...prevValues,
                        itemImages: reordered,
                    }));
                };

                const handleRemoveImage = (index: number) => {
                    const newFormImages = (values.itemImages || []).filter(
                        (_: string, i: number) => i !== index
                    );
                    setFieldValue("itemImages", newFormImages);
                    setCreateQuickLinkFormValues((prevValues: any) => ({
                        ...prevValues,
                        itemImages: newFormImages,
                    }));
                };

                return (
                    <Form style={{ padding: "0 39px" }} >
                        <Box sx={{ fontFamily: theme.typography.fontFamily }}>
                            {/* Item Image - At Top */}
                            <Box sx={{ mb: 2 }}>
                                {values.itemImages.length === 0 ? (
                                    <Button
                                        component="label"
                                        sx={{
                                            border: "2px dashed #C4C4C4",
                                            height: 180,
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderRadius: "12px",
                                            backgroundColor: "transparent",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "0.85rem", textAlign: "center" }}>
                                            Tap to Upload a photo
                                        </Typography>
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                        />
                                    </Button>
                                ) : (
                                    <Box
                                        component="label"
                                        sx={{
                                            display: "block",
                                            cursor: "pointer",
                                            position: "relative",
                                            maxWidth: "537px",
                                            aspectRatio: "16 / 9",
                                            mx: "auto",
                                            overflow: "hidden",
                                            borderRadius: "12px",
                                        }}
                                    >
                                        <img
                                            src={`${awsImageRootUrl}/${values.itemImages[0]}`}
                                            alt="Item"
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file && file.type.startsWith("image/")) {
                                                    const reader = new FileReader();
                                                    reader.onload = async (event: ProgressEvent<FileReader>) => {
                                                        if (event.target?.result) {
                                                            setImageSrc(event.target.result as string);
                                                            setOpenCropper(true);
                                                            // Clear the current image so the new one replaces it
                                                            setFieldValue("itemImages", []);
                                                            setCreateQuickLinkFormValues((prev) => ({
                                                                ...prev,
                                                                itemImages: [],
                                                            }));
                                                        }
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                        />
                                    </Box>
                                )}

                                <Typography
                                    sx={{
                                        fontSize: "17px",
                                        textAlign: "center",
                                        color: "text.secondary",
                                        mt: "8px",
                                    }}
                                >
                                    Click the image to select a different photo.
                                </Typography>

                                <canvas
                                    ref={canvasRef}
                                    width={500}
                                    height={500}
                                    style={{ display: "none" }}
                                />

                                {errors.itemImages && touched.itemImages && (
                                    <Typography color="red" fontSize="12px">
                                        {errors.itemImages}
                                    </Typography>
                                )}
                            </Box>

                            {/* Item Name */}
                            <Box>
                                <Label label="Item Name" required={true} size="24px" margin="0px" />
                                <Field
                                    as={TextField}
                                    name="item_name"
                                    fullWidth
                                    size="small"
                                    onChange={handleChange}
                                    error={touched.item_name && Boolean(errors.item_name)}
                                    multiline
                                    minRows={1}
                                    maxRows={2}
                                    sx={{ height: "46px", borderRadius: "8px" }}
                                />
                                {errors.item_name && touched.item_name && (
                                    <Typography color="red" fontSize="12px">
                                        {errors.item_name}
                                    </Typography>
                                )}
                            </Box>

                            {/* Date, Start Time, and Quantity in a row */}
                            <Box sx={{ display: "flex", gap: "32px", pt: "4px" }}>
                                {/* Date Field */}
                                <Box sx={{ display: "flex", flexDirection: "column", width: 160 }}>
                                    <Label label="Date" required={true} size="17px" margin="0px" />
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            minDate={new Date()}
                                            value={
                                                values.date
                                                    ? (() => {
                                                        const d = new Date(values.date as string);
                                                        return Number.isNaN(d.getTime()) ? null : d;
                                                    })()
                                                    : null
                                            }
                                            onChange={(newValue: Date | null) => {
                                                const isValidDate =
                                                    newValue instanceof Date &&
                                                    !Number.isNaN(newValue.getTime());

                                                const formattedDate = isValidDate
                                                    ? `${newValue.getFullYear()}-${String(newValue.getMonth() + 1).padStart(2, '0')}-${String(newValue.getDate()).padStart(2, '0')}`
                                                    : null;
                                                setFieldValue("date", formattedDate);
                                                setCreateQuickLinkFormValues((prevValues: any) => ({
                                                    ...prevValues,
                                                    date: formattedDate,
                                                }));
                                            }}
                                            slots={{
                                                openPickerIcon: () => <img src={CalendarIcon} alt="calendar" style={{ width: 16, height: 16 }} />,
                                            }}
                                            slotProps={{
                                                field: {
                                                    readOnly: true,
                                                },
                                                textField: {
                                                    fullWidth: true,
                                                    size: "small",
                                                    error: touched.date && Boolean(errors.date),
                                                    placeholder: "",
                                                    sx: { cursor: 'pointer' },
                                                    onClick: () => setDatePickerOpen(true),
                                                },
                                                inputAdornment: {
                                                    position: 'start',
                                                },
                                            }}
                                            open={datePickerOpen}
                                            onOpen={() => setDatePickerOpen(true)}
                                            onClose={() => setDatePickerOpen(false)}
                                        />
                                    </LocalizationProvider>
                                    {errors.date && touched.date && (
                                        <Typography color="red" fontSize="12px">
                                            {errors.date}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Start Time */}
                                <Box sx={{ display: "flex", flexDirection: "column", width: 160 }}>
                                    <Label label="Start Time" required={true} size="17px" margin="0px" />
                                    <Field
                                        as={TextField}
                                        name="start_time"
                                        fullWidth
                                        select
                                        size="small"
                                        value={values.start_time || ""}
                                        onChange={(e: any) => {
                                            const { value } = e.target;
                                            const formattedValue = value || null;
                                            setFieldValue("start_time", formattedValue);
                                            setCreateQuickLinkFormValues((prevValues: any) => ({
                                                ...prevValues,
                                                start_time: formattedValue,
                                            }));
                                        }}
                                        error={touched.start_time && Boolean(errors.start_time)}
                                        sx={{ borderRadius: "8px" }}
                                        SelectProps={{
                                            MenuProps: {
                                                PaperProps: {
                                                    sx: {
                                                        '& .MuiMenuItem-root:hover': {
                                                            backgroundColor: '#328734',
                                                            color: '#fff',
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        {timeOptions.map((time: string) => (
                                            <MenuItem key={time} value={time}>
                                                {time}
                                            </MenuItem>
                                        ))}
                                    </Field>
                                    {errors.start_time && touched.start_time && (
                                        <Typography color="red" fontSize="12px">
                                            {errors.start_time}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Quantity  starting at 1 */}
                                <Box sx={{ display: "flex", flexDirection: "column", width: 160 }}>
                                    <Label label="Quantity" required={true} size="17px" margin='0' />
                                    <Field
                                        as={TextField}
                                        type="number"
                                        name="quantity"
                                        fullWidth
                                        size="small"
                                        value={values.quantity}
                                        onFocus={() => {
                                            if (values.quantity === 1 && firstTimeQuantityClick) {
                                                setFieldValue("quantity", "");
                                                setFirstTimeQuantityClick(false);
                                                setCreateQuickLinkFormValues((prevValues: any) => ({
                                                    ...prevValues,
                                                    quantity: "",
                                                }));
                                            }
                                        }}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const val = e.target.value === "" ? "" : Number(e.target.value);
                                            setFieldValue("quantity", val);
                                            setCreateQuickLinkFormValues((prevValues: any) => ({
                                                ...prevValues,
                                                quantity: val,
                                            }));
                                        }}
                                        error={touched.quantity && Boolean(errors.quantity)}
                                        inputProps={{ min: 1 }}
                                    />
                                    {errors.quantity && touched.quantity && (
                                        <Typography color="red" fontSize="12px">
                                            {errors.quantity}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Duration - Optional */}
                            <Box sx={{ mt: "4px" }}>
                                <Label label="Duration" required={false} size="17px" margin="0px" />
                                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    {/* Days */}
                                    <Box sx={{ width: 120 }}>
                                        <Field
                                            as={TextField}
                                            name="duration_days"
                                            fullWidth
                                            select
                                            size="small"
                                            placeholder="Days"
                                            value={values.duration_days ?? ""}
                                            onChange={(e: any) => {
                                                const val = e.target.value === "" ? null : Number(e.target.value);
                                                handleSelectChange("duration_days", val);
                                                // Clear hours and minutes when days is selected
                                                if (val !== null) {
                                                    handleSelectChange("duration_hours", null);
                                                    handleSelectChange("duration_minutes", null);
                                                }
                                            }}
                                            SelectProps={{
                                                displayEmpty: true,
                                                renderValue: (selected: any) => {
                                                    if (selected === "" || selected === null || selected === undefined) {
                                                        return <span style={{ color: "#8D8D8D" }}>Days</span>;
                                                    }
                                                    return selected;
                                                },
                                            }}
                                            sx={{ borderRadius: "8px" }}
                                        >
                                            <MenuItem value="" disabled>
                                                Days
                                            </MenuItem>
                                            {dayOptions.map((day: number) => (
                                                <MenuItem key={day} value={day}>
                                                    {day}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Box>

                                    {/* "or" separator */}
                                    <Typography sx={{ color: "#8D8D8D", fontWeight: 500, mx: 1 }}>or</Typography>

                                    {/* Hours */}
                                    <Box sx={{ width: 120 }}>
                                        <Field
                                            as={TextField}
                                            name="duration_hours"
                                            fullWidth
                                            select
                                            size="small"
                                            value={values.duration_hours ?? ""}
                                            onChange={(e: any) => {
                                                const val = e.target.value === "" ? null : Number(e.target.value);
                                                handleSelectChange("duration_hours", val);
                                                // Clear days when hours is selected
                                                if (val !== null) {
                                                    handleSelectChange("duration_days", null);
                                                }
                                            }}
                                            SelectProps={{
                                                displayEmpty: true,
                                                renderValue: (selected: any) => {
                                                    if (selected === "" || selected === null || selected === undefined) {
                                                        return <span style={{ color: "#8D8D8D" }}>Hours</span>;
                                                    }
                                                    return selected;
                                                },
                                            }}
                                            sx={{ borderRadius: "8px" }}
                                        >
                                            <MenuItem value="">Hours</MenuItem>
                                            {hourOptions.map((hour: number) => (
                                                <MenuItem key={hour} value={hour}>
                                                    {hour}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Box>
                                    {/* Minutes */}
                                    <Box sx={{ width: 120 }}>
                                        <Field
                                            as={TextField}
                                            name="duration_minutes"
                                            fullWidth
                                            select
                                            size="small"
                                            value={values.duration_minutes ?? ""}
                                            onChange={(e: any) => {
                                                const val = e.target.value === "" ? null : Number(e.target.value);
                                                handleSelectChange("duration_minutes", val);
                                                // Clear days when minutes is selected
                                                if (val !== null) {
                                                    handleSelectChange("duration_days", null);
                                                }
                                            }}
                                            SelectProps={{
                                                displayEmpty: true,
                                                renderValue: (selected: any) => {
                                                    if (selected === "" || selected === null || selected === undefined) {
                                                        return <span style={{ color: "#8D8D8D" }}>Minutes</span>;
                                                    }
                                                    return selected;
                                                },
                                            }}
                                            sx={{ borderRadius: "8px" }}
                                        >
                                            <MenuItem value="">Minutes</MenuItem>
                                            {minuteOptions.map((minute: number) => (
                                                <MenuItem key={minute} value={minute}>
                                                    {minute}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Description */}
                            <Box mt="4px">
                                <Label label="Description" required={true} size="17px" margin="0px" />
                                <Field
                                    as={TextField}
                                    name="description"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    onChange={handleChange}
                                    error={touched.description && Boolean(errors.description)}
                                    sx={{ borderRadius: "8px" }}
                                />
                                {errors.description && touched.description && (
                                    <Typography color="red" fontSize="12px">
                                        {errors.description}
                                    </Typography>
                                )}
                            </Box>

                            {/* What's Included */}
                            <Box mt="4px">
                                <Label label="What's Included" required={true} size="17px" margin="0px" />
                                <Field
                                    as={TextField}
                                    name="whats_included"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    onChange={handleChange}
                                    error={touched.whats_included && Boolean(errors.whats_included)}
                                    sx={{ borderRadius: "8px" }}
                                />
                                {errors.whats_included && touched.whats_included && (
                                    <Typography color="red" fontSize="12px">
                                        {errors.whats_included}
                                    </Typography>
                                )}
                            </Box>

                            {/* Cancellation Policy */}
                            <Box mt="4px">
                                <Label label="Cancellation Policy" required={true} size="17px" margin="0px" />
                                <Field
                                    as={TextField}
                                    name="cancellation_policy"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    onChange={handleChange}
                                    error={
                                        touched.cancellation_policy &&
                                        Boolean(errors.cancellation_policy)
                                    }
                                    sx={{ borderRadius: "8px" }}
                                />
                                {errors.cancellation_policy && touched.cancellation_policy && (
                                    <Typography color="red" fontSize="12px">
                                        {errors.cancellation_policy}
                                    </Typography>
                                )}
                            </Box>

                            {/* Price and Commission */}
                            <Box sx={{ display: "flex", gap: "10px" }} mt="4px">
                                {/* Price */}
                                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                    <Label label="Price" required={true} size="17px" margin="0px" />
                                    <Field
                                        as={TextField}
                                        type="text"
                                        name="price"
                                        fullWidth
                                        size="small"
                                        onChange={(e: any) => handleNumericChange(e, "price")}
                                        error={touched.price && Boolean(errors.price)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">$</InputAdornment>
                                            ),
                                        }}
                                        sx={{ borderRadius: "8px" }}
                                    />
                                </Box>

                                {/* Commission - No spinner arrows */}
                                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                    <Label label="Commission" required={true} size="17px" margin="0px" />
                                    <Field
                                        as={TextField}
                                        type="text"
                                        name="commission"
                                        fullWidth
                                        size="small"
                                        onChange={(e: any) => handleNumericChange(e, "commission")}
                                        error={touched?.commission && Boolean(errors?.commission)}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">%</InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            borderRadius: "8px",
                                            "& input[type=number]": {
                                                MozAppearance: "textfield",
                                            },
                                            "& input[type=number]::-webkit-outer-spin-button": {
                                                WebkitAppearance: "none",
                                                margin: 0,
                                            },
                                            "& input[type=number]::-webkit-inner-spin-button": {
                                                WebkitAppearance: "none",
                                                margin: 0,
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Your Payout Calculation Box */}
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 2,
                                    backgroundColor: "#D6D6DA",
                                    borderRadius: "8px",
                                    color: "white",
                                }}
                            >
                                <Typography sx={{ fontSize: "17px", fontWeight: "700", color: "#595959", mb: "10px" }}>
                                    Your Pay-out:
                                </Typography>
                                <Typography sx={{ fontSize: "17px", fontWeight: "400" }}>
                                    {calculatePayout(values.price || 0, values.commission || 0)}
                                </Typography>
                            </Box>

                            {/* Customer Added Value Section */}
                            <Box sx={{ mt: "16px" }}>
                                <Typography sx={{ fontSize: "15px", fontWeight: "600", color: "#000" }}>
                                    Customer Added Value <Typography component="span" sx={{ fontStyle: "italic" }}>(Optional)</Typography>
                                </Typography>
                                <Box
                                    sx={{
                                        mt: "18px",
                                        textAlign: "center",
                                    }}
                                >
                                    <Typography sx={{ fontWeight: "700", fontSize: "24px", mb: 1 }}>
                                        Book Now and Receive
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
                                        <Typography sx={{ fontSize: "1.5rem" }}>🔥</Typography>
                                        <Field
                                            as={TextField}
                                            name="customer_added_value"
                                            size="small"
                                            placeholder="10% off regular price (normally $300)"
                                            onChange={handleChange}
                                            sx={{
                                                width: "64%",
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px",
                                                },
                                                "& .MuiInputBase-input": {
                                                    textAlign: "center", // Center input text
                                                    "&::placeholder": {
                                                        textAlign: "center", // Center placeholder text
                                                    },
                                                },
                                            }}
                                        />
                                        <Typography sx={{ fontSize: "1.5rem" }}>🔥</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Cropper Dialog */}
                            <Dialog
                                open={openCropper}
                                onClose={() => setOpenCropper(false)}
                                maxWidth="sm"
                                fullWidth
                            >
                                <DialogContent
                                    sx={{ position: "relative", height: 400, background: "#333" }}
                                >
                                    {imageSrc && (
                                        <Cropper
                                            image={imageSrc}
                                            crop={crop}
                                            zoom={zoom}
                                            aspect={16 / 9}
                                            cropShape="rect"
                                            rotation={0}
                                            showGrid={true}
                                            onCropChange={setCrop}
                                            onZoomChange={setZoom}
                                            onCropComplete={onCropComplete}
                                        />
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setOpenCropper(false)}>Cancel</Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => handleCropConfirm(setFieldValue)}
                                    >
                                        Crop & Upload
                                    </Button>
                                </DialogActions>
                            </Dialog>

                            <Box sx={{ display: "flex", justifyContent: "center", mt: "38px" }}>
                                <Button
                                    type="submit"
                                    size="small"
                                    variant="contained"
                                    sx={{
                                        width: "358px",
                                        height: "50px",
                                        fontSize: "17px",
                                        fontWeight: "600",
                                        paddingY: "12px",
                                        borderRadius: "10px",
                                        backgroundColor: "#4CAF50",
                                        "&:hover": {
                                            backgroundColor: "#45a049",
                                        },
                                    }}
                                >
                                    Create and Share Booking Link
                                </Button>
                            </Box>
                        </Box>
                    </Form>
                )
            }}
        </Formik >
    )
}
